<!-- Time Picker Component with Localization Support -->
<template>
  <div
    v-click-outside="closePickerIfOpen"
    class="custom-time-picker"
  >
    <!-- Time Input Display -->
    <div
      class="time-input-display"
      @click="handleInputClick"
    >
      <span class="time-icon">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
          />
          <path d="M12 6v6l4 2" />
        </svg>
      </span>
      <input
        v-model="displayTime"
        type="text"
        class="time-display-input"
        :placeholder="placeholder"
        readonly
        @focus="handleInputFocus"
        @blur="handleInputBlur"
        @input="handleManualInput"
      >
    </div>

    <!-- Time Picker Dropdown -->
    <div
      v-if="isOpen"
      class="time-picker-dropdown"
    >
      <div class="time-spinners">
        <div class="time-unit-container" :class="{ 'time-unit-container-24h': !use12Hour }">
          <!-- Hours -->
          <div class="spinner-column hours">
            <button
              type="button"
              class="spinner-button"
              @click="adjustHours(1)"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path d="M18 15l-6-6-6 6" />
              </svg>
            </button>
            <input
              v-model="hours"
              type="text"
              class="time-input"
              maxlength="2"
              @input="handleHourInput"
              @blur="formatHours"
              @focus="handleFocusHourInput"
            >
            <button
              type="button"
              class="spinner-button"
              @click="adjustHours(-1)"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
          </div>

          <!-- Separator -->
          <div class="time-separator">
            :
          </div>

          <!-- Minutes -->
          <div class="spinner-column minutes">
            <button
              type="button"
              class="spinner-button"
              @click="adjustMinutes(1)"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path d="M18 15l-6-6-6 6" />
              </svg>
            </button>
            <input
              v-model="minutes"
              type="text"
              class="time-input"
              maxlength="2"
              @input="handleMinuteInput"
              @blur="formatMinutes"
              @focus="handleFocusMinuteInput"
            >
            <button
              type="button"
              class="spinner-button"
              @click="adjustMinutes(-1)"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
          </div>
        </div>

        <!-- AM/PM -->
        <div
          v-if="use12Hour"
          class="spinner-column period"
        >
          <button
            type="button"
            class="spinner-button"
            @click="togglePeriod"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path d="M18 15l-6-6-6 6" />
            </svg>
          </button>
          <div
            class="period-display"
            @click="togglePeriod"
          >
            {{ localizedPeriods[period] }}
          </div>
          <button
            type="button"
            class="spinner-button"
            @click="togglePeriod"
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Control Buttons -->
      <div class="picker-controls">
        <button
          type="button"
          class="control-button reset"
          @click="resetTime"
        >
          {{ $t('button.reset') || 'Reset' }}
        </button>
        <button
          type="button"
          class="control-button close"
          @click="closePicker"
        >
          {{ $t('button.timePickerConfirm') || 'Confirm' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LocalizedTimePicker',
  directives: {
    clickOutside: {
      bind(el, binding) {
        el.clickOutsideEvent = event => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event)
          }
        }
        document.addEventListener('click', el.clickOutsideEvent)
      },
      unbind(el) {
        document.removeEventListener('click', el.clickOutsideEvent)
      },
    },
  },
  props: {
    value: {
      type: String,
      default: null,
    },
    locale: {
      type: String,
      default: 'en-US',
    },
    placeholder: {
      type: String,
      default: 'Select time',
    },
    use24Hours: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isOpen: false,
      hours: '--',
      minutes: '--',
      period: 'AM',
      displayTime: '',
      localizedPeriods: {
        AM: 'AM',
        PM: 'PM',
      },
      use12Hour: true,
      isManualInput: false,
    }
  },
  watch: {
    value: {
      handler(newVal) {
        if (!this.isManualInput && newVal) {
          this.initializeFromValue()
        }
        this.isManualInput = false
      },
    },
    locale: {
      handler() {
        this.initializeLocale()
        this.updateDisplayTime()
      },
      immediate: true,
    },
    use24Hours: {
      handler() {
        this.initializeLocale()
        this.initializeFromValue()
      },
    },
    isOpen(newVal) {
      if (!newVal && this.displayTime === '') {
        this.resetTime()
      } else {
        this.initializeFromValue()
      }
    },
  },
  created() {
    this.initializeLocale()
    this.initializeFromValue()
  },
  methods: {
    initializeLocale() {
      try {
        const formatter = new Intl.DateTimeFormat(this.locale)
        const options = formatter.resolvedOptions()

        // Use the prop value if provided, otherwise use locale default
        this.use12Hour = this.use24Hours === null ? (options.hour12 ?? true) : !this.use24Hours

        // Handle special cases for specific locales
        if (this.locale.startsWith('zh')) {
          this.localizedPeriods = { AM: '上午', PM: '下午' }
        } else if (this.locale.startsWith('km')) {
          this.localizedPeriods = { AM: 'ព្រឹក', PM: 'ល្ងាច' }
        } else {
          const date = new Date(2000, 0, 1, 0, 0, 0)
          const pmDate = new Date(2000, 0, 1, 12, 0, 0)

          const amString = new Intl.DateTimeFormat(this.locale, {
            hour: 'numeric',
            hour12: true,
          }).format(date)

          const pmString = new Intl.DateTimeFormat(this.locale, {
            hour: 'numeric',
            hour12: true,
          }).format(pmDate)

          this.localizedPeriods = {
            AM: amString.replace(/[0-9:]/g, '').trim(),
            PM: pmString.replace(/[0-9:]/g, '').trim(),
          }
        }
      } catch (e) {
        console.error('Error initializing locale:', e)
      }
    },
    initializeFromValue() {
      if (!this.value) {
        this.resetTime()
        return
      }

      try {
        const [hours, minutes] = this.value.split(':')
        let hour = parseInt(hours, 10)

        if (this.use12Hour) {
          this.period = hour >= 12 ? 'PM' : 'AM'
          hour = hour % 12 || 12
        }

        this.hours = hour.toString()
        this.minutes = minutes.toString().padStart(2, '0')
        this.updateDisplayTime()
      } catch (e) {
        console.error('Error initializing from value:', e)
        this.resetTime()
      }
    },
    handleManualInput(event) {
      const { value } = event.target
      this.isManualInput = true

      if (!value) {
        this.resetTime()
        return
      }

      // Updated regex to handle 24-hour format (e.g., "14:30" without AM/PM)
      const timePattern = this.use12Hour
        ? /^(\d{1,2})[:.]?(\d{0,2})\s*(am|pm|AM|PM)?$/
        : /^(\d{1,2})[:.]?(\d{0,2})$/
      const match = value.match(timePattern)

      if (match) {
        let [, hours, minutes, period] = match
        hours = parseInt(hours, 10)
        minutes = minutes ? parseInt(minutes, 10) : 0

        // Handle hours > 12 and minutes > 59
        if (this.use12Hour) {
          if (period) {
            this.period = period.toUpperCase()
          }
          if (hours > 12) {
            hours = 12
          }
          if (hours === 0) hours = 12
        } else {
          // For 24-hour format
          if (hours > 23) {
            hours = 23
          }
        }

        if (minutes > 59) {
          minutes = 59
        }

        if (this.isValidHours(hours) && this.isValidMinutes(minutes)) {
          this.hours = hours.toString()
          this.minutes = minutes.toString()
          this.emitTime()
        }
      }
    },
    isValidHours(hours) {
      return this.use12Hour
        ? hours >= 1 && hours <= 12
        : hours >= 0 && hours <= 23
    },
    isValidMinutes(minutes) {
      return minutes >= 0 && minutes <= 59
    },
    handleInputClick() {
      if (!this.isOpen) {
        this.togglePicker()
        this.formatHours()
        this.formatMinutes()
      }
    },
    handleInputFocus() {
      // Allow manual input without opening dropdown
    },
    handleInputBlur() {
      this.updateDisplayTime()
    },
    closePickerIfOpen(event) {
      if (this.isOpen && !event.target.closest('.time-picker-dropdown')) {
        this.closePicker()
      }
    },
    handleHourInput(event) {
      const { value } = event.target
      // First check if event.target exists and has a value property
      if (!value) {
        this.hours = ''
        return
      }

      // Check if value contains any non-numeric characters (except --)
      if (!/^\d+$/.test(value) && value !== '--') {
        // Remove non-numeric characters
        const cleanedVal = value.replace(/[^0-9]/g, '')
        event.target.value = cleanedVal
        // If no numbers remain after cleaning, reset hours
        if (!cleanedVal) {
          this.hours = ''
          return
        }
      }

      if (value === '--') {
        this.hours = ''
        return
      }

      this.isManualInput = true
      let numVal = parseInt(value, 10)
      if (Number.isNaN(numVal)) return

      const maxHours = this.use12Hour ? 12 : 23
      const minHours = this.use12Hour ? 1 : 0

      // Round numbers greater than maxHours down to maxHours
      if (numVal > maxHours) {
        numVal = maxHours
      }

      if (numVal >= minHours && numVal <= maxHours) {
        this.hours = numVal.toString()
        this.emitTime()
      }
    },
    handleMinuteInput(event) {
      const { value } = event.target
      // Check if event.target exists and has a value property
      if (!value) {
        this.minutes = ''
        return
      }

      // Check if value contains any non-numeric characters (except --)
      if (!/^\d+$/.test(value) && value !== '--') {
        // Remove non-numeric characters
        const cleanedVal = value.replace(/[^0-9]/g, '')
        event.target.value = cleanedVal
        // If no numbers remain after cleaning, reset minutes
        if (!cleanedVal) {
          this.minutes = ''
          return
        }
      }

      if (value === '--') {
        this.minutes = ''
        return
      }

      this.isManualInput = true
      let numVal = parseInt(value, 10)
      if (Number.isNaN(numVal)) return

      // Round numbers greater than 59 down to 59
      if (numVal > 59) {
        numVal = 59
      }

      if (numVal >= 0 && numVal <= 59) {
        this.minutes = numVal.toString()
        this.emitTime()
      }
    },
    adjustHours(increment) {
      let hour = this.hours === '--'
        ? this.use12Hour
          ? 12
          : 0
        : parseInt(this.hours || 12, 10)
      const maxHours = this.use12Hour ? 12 : 23
      const minHours = this.use12Hour ? 1 : 0

      hour = increment > 0
        ? hour === maxHours
          ? minHours
          : hour + 1
        : hour === minHours
          ? maxHours
          : hour - 1

      this.hours = hour.toString()
      this.emitTime()
    },
    adjustMinutes(increment) {
      let minute = this.minutes === '--' ? 0 : parseInt(this.minutes, 10)
      minute = (minute + increment + 60) % 60 || 0
      this.minutes = minute.toString()
      this.emitTime()
    },
    togglePeriod() {
      this.period = this.period === 'AM' ? 'PM' : 'AM'
      this.emitTime()
    },
    formatHours() {
      if (!this.hours || this.hours === '--') {
        this.hours = '--'
        return
      }

      const hour = parseInt(this.hours, 10)
      if (Number.isNaN(hour)) return

      this.hours = hour.toString()
      this.emitTime()
    },
    formatMinutes() {
      if (!this.minutes || this.minutes === '--') {
        this.minutes = '--'
        return
      }

      const minutes = parseInt(this.minutes, 10)
      if (Number.isNaN(minutes)) return

      this.minutes = minutes.toString().padStart(2, '0')
      this.emitTime()
    },
    resetTime() {
      // For Khmer locale, show -- instead of NaN
      const placeholder = this.locale.startsWith('km') ? '--' : '--'
      this.hours = placeholder
      this.minutes = placeholder
      this.period = 'AM'
      this.displayTime = ''
      this.$emit('input', '')
    },
    togglePicker() {
      this.isOpen = !this.isOpen
    },
    closePicker() {
      this.isOpen = false
    },
    updateDisplayTime() {
      if (this.hours === '--' || this.minutes === '--') {
        this.displayTime = ''
        return
      }

      try {
        // Create a valid 24-hour time string
        const timeString = this.formatTimeString()

        // Validate the time components before creating Date object
        const [hours, minutes] = timeString.split(':').map(Number)
        if (
          Number.isNaN(hours)
            || Number.isNaN(minutes)
            || hours < 0
            || hours > 23
            || minutes < 0
            || minutes > 59
        ) {
          throw new Error('Invalid time components')
        }

        // Create date object for current day to avoid timezone issues
        const now = new Date()
        const time = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          hours,
          minutes,
        )

        if (Number.isNaN(time.getTime())) {
          throw new Error('Invalid date created')
        }

        if (this.locale.startsWith('km')) {
          this.displayTime = this.formatKhmerTime(time)
        } else {
          this.displayTime = new Intl.DateTimeFormat(this.locale, {
            hour: 'numeric',
            minute: 'numeric',
            hour12: this.use12Hour,
          }).format(time)
        }
      } catch (e) {
        console.error('Error updating display time:', e)
        this.displayTime = ''
      }
    },
    formatTimeString() {
      if (this.hours === '--' || this.minutes === '--') {
        return ''
      }

      let formattedHours = parseInt(this.hours, 10)
      if (Number.isNaN(formattedHours)) {
        return ''
      }

      // Convert to 24-hour format if needed
      if (this.use12Hour) {
        if (this.period === 'PM' && formattedHours !== 12) {
          formattedHours += 12
        } else if (this.period === 'AM' && formattedHours === 12) {
          formattedHours = 0
        }
      }

      // Validate hours range
      if (formattedHours < 0 || formattedHours > 23) {
        return ''
      }

      const minutes = parseInt(this.minutes, 10)
      if (Number.isNaN(minutes) || minutes < 0 || minutes > 59) {
        return ''
      }

      return `${formattedHours
        .toString()
        .padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
    },
    formatKhmerTime(time) {
      const hours = time.getHours()
      const minutes = time.getMinutes()
      const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours
      const period = hours >= 12 ? 'ល្ងាច' : 'ព្រឹក'
      const formattedHours = displayHours.toString()
      const formattedMinutes = minutes.toString().padStart(2, '0')
      return `${formattedHours}:${formattedMinutes} ${period}`
    },
    emitTime() {
      if (this.hours === '--' || this.minutes === '--') {
        this.$emit('input', '')
        return
      }

      this.$emit('input', this.formatTimeString())
      this.updateDisplayTime()
    },
    handleFocusHourInput() {
      if (this.hours === '--') {
        this.hours = ''
      }
    },
    handleFocusMinuteInput() {
      if (this.minutes === '--') {
        this.minutes = ''
      }
    },
  },
}
</script>

<style scoped>
  .custom-time-picker {
    position: relative;
    width: 100%;
  }

  .time-input-display {
    display: flex;
    align-items: center;
    padding: 0.5rem;
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    background: white;
    cursor: pointer;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  }

  .time-input-display:hover {
    border-color: #adb5bd;
  }

  .time-icon {
    color: #6c757d;
    margin-right: 0.5rem;
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }

  .time-display-input {
    border: none;
    background: transparent;
    width: 100%;
    color: #212529;
    font-size: 1rem;
    padding: 0;
    cursor: pointer;
  }

  .time-display-input::placeholder {
    color: #6c757d;
  }

  .time-display-input:focus {
    outline: none;
    cursor: text;
  }

  .time-picker-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    z-index: 1000;
    background: white;
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    min-width: 200px;
  }

  .time-spinners {
    display: flex;
    align-items: stretch;
    padding: 1rem;
    background: #fff;
    border-radius: 4px;
    justify-content: space-around;
  }

  .time-unit-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin: 0 4px;
  }

  .time-unit-container-24h {
    width: 100%;
  }

  .time-unit-container-24h .time-input {
    min-width: 60px;
  }

  .time-unit-container-24h .spinner-column {
    width: 100%;
  }

  .spinner-column {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 40px;
  }

  .spinner-button {
    border: none;
    background: transparent;
    color: #6c757d;
    padding: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 4px;
    transition: all 0.2s ease;
  }

  .spinner-button:hover {
    background-color: #f8f9fa;
    color: #8d5185;
  }

  /* Make time inputs wider in 24h mode for better readability */
  .time-input {
    width: 40px;
    height: 28px;
    text-align: center;
    border: 1px solid transparent;
    background: transparent;
    font-size: 1rem;
    padding: 0;
    margin: 4px 0;
    color: #212529;
    border-radius: 4px;
  }

  /* Adjust time unit container when in 24h mode */
  .time-unit-container:only-child {
    justify-content: center;
    width: 100%;
  }

  /* Make spinner columns wider in 24h mode */
  .time-unit-container:only-child .spinner-column {
    min-width: 50px;
  }

  .time-input:focus {
    outline: none;
    border-color: #8d5185;
    background-color: #f8f9fa;
  }

  .time-separator {
    font-size: 1rem;
    color: #212529;
    user-select: none;
    padding: 0 4px;
    line-height: 28px;
  }

  .period-display {
    width: 3.5rem;
    text-align: center;
    padding: 4px 8px;
    margin: 4px 0;
    font-size: 1rem;
    cursor: pointer;
    user-select: none;
    border-radius: 4px;
    transition: background-color 0.2s ease;
  }

  .period-display:hover {
    background-color: #f8f9fa;
  }

  .picker-controls {
    display: flex;
    justify-content: space-between;
    border-top: 1px solid #dee2e6;
    padding: 0.75rem 1rem;
    background-color: #f8f9fa;
    border-radius: 0 0 0.25rem 0.25rem;
  }

  .control-button {
    padding: 0.375rem 0.75rem;
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
    background-color: white;
    color: #212529;
    cursor: pointer;
    font-size: 0.875rem;
    line-height: 1.5;
    transition: all 0.15s ease-in-out;
  }

  .control-button.reset {
    color: #6c757d;
  }

  .control-button.reset:hover {
    color: #fff;
    background-color: #6c757d;
    border-color: #6c757d;
  }

  .control-button.close {
    color: #fff;
    background-color: #8d5185;
    border-color: #8d5185;
    opacity: 1;
  }

  .control-button.close:hover {
    color: #fff;
    background-color: #8d5185;
    border-color: #8d5185;
  }

  /* Hide number input spinners */
  .time-input::-webkit-inner-spin-button,
  .time-input::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .time-input[type='number'] {
    -moz-appearance: textfield;
  }

  @media (max-width: 576px) {
    .time-picker-dropdown {
      position: fixed;
      top: auto;
      bottom: 0;
      left: 0;
      right: 0;
      margin: 0;
      border-radius: 0.5rem 0.5rem 0 0;
      box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    }

    .time-spinners {
      justify-content: center;
    }
  }
</style>
